/**
 * Хамааралгүй, хамгийн бага XLSX бичигч — нэг хуудас.
 *
 * ЯАГААД: CSV-д баганын өргөн, тод гарчиг, мөнгөний формат байхгүй —
 * хүлээн авалтын баримт Excel-д тасархай шошго, `########` огноотой
 * нээгдэв (2026-10-03). `exceljs` нэмэхгүй (шийдвэр 2026-08-21) тул
 * OOXML-ийн шаардлагатай 6 файлыг өөрсдөө бичиж, шахалтгүй (STORE)
 * ZIP-д хийнэ.
 *
 * Цэвэр код — Buffer, zlib хэрэглэхгүй, хөтөч ба Node хоёуланд ажиллана.
 *
 * ⚠️ Текст бүр `inlineStr` — sharedStrings хэрэггүй. Баркод текст тул
 *    тэргүүний 0 хадгалагдаж, 12 оронтой код `4.46E+11` болохгүй.
 * ⚠️ Тоо нь string-ээр орж ирж болно (`"450000.00"`) — мөнгийг Float
 *    болгохгүйгээр XML-д шууд бичнэ (CLAUDE.md №3).
 */
/** `styles.xml`-ийн `cellXfs` дахь индекс */
const STYLE_INDEX = { title: 1, label: 2, header: 3, money: 4, alert: 5 };
const NS = 'http://schemas.openxmlformats.org/spreadsheetml/2006/main';
const REL = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships';
const PKG_REL = 'http://schemas.openxmlformats.org/package/2006/relationships';
const HEAD = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n';
const NUMBER = /^-?\d+(\.\d+)?$/;
/** XML 1.0-д хориотой удирдах тэмдэгтийг хасаад escape хийнэ. */
function esc(s) {
    return s
        // eslint-disable-next-line no-control-regex
        .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}
/** 0 → A, 25 → Z, 26 → AA */
function colName(i) {
    let n = i + 1;
    let s = '';
    while (n > 0) {
        s = String.fromCharCode(65 + ((n - 1) % 26)) + s;
        n = Math.floor((n - 1) / 26);
    }
    return s;
}
function cellXml(c, ref) {
    const s = c.style ? ` s="${STYLE_INDEX[c.style]}"` : '';
    if ('num' in c) {
        const v = String(c.num);
        if (!NUMBER.test(v))
            throw new Error(`XLSX: тоо биш утга ${ref}: ${v}`);
        return `<c r="${ref}"${s}><v>${v}</v></c>`;
    }
    return `<c r="${ref}"${s} t="inlineStr"><is><t xml:space="preserve">${esc(c.text)}</t></is></c>`;
}
function sheetXml(sheet) {
    const cols = sheet.widths
        .map((w, i) => `<col min="${i + 1}" max="${i + 1}" width="${w}" customWidth="1"/>`)
        .join('');
    const rows = sheet.rows
        .map((cells, r) => {
        const body = cells.map((c, i) => (c ? cellXml(c, `${colName(i)}${r + 1}`) : '')).join('');
        return body ? `<row r="${r + 1}">${body}</row>` : '';
    })
        .join('');
    const merges = sheet.merges?.length
        ? `<mergeCells count="${sheet.merges.length}">${sheet.merges.map((m) => `<mergeCell ref="${m}"/>`).join('')}</mergeCells>`
        : '';
    // Хэвлэхэд A4-ийн өргөнд багтаана — баримтыг цаасаар гарын үсэг зуруулдаг
    return `${HEAD}<worksheet xmlns="${NS}"><sheetPr><pageSetUpPr fitToPage="1"/></sheetPr>`
        + `<cols>${cols}</cols><sheetData>${rows}</sheetData>${merges}`
        + '<pageMargins left="0.5" right="0.5" top="0.6" bottom="0.6" header="0.3" footer="0.3"/>'
        + '<pageSetup paperSize="9" orientation="portrait" fitToWidth="1" fitToHeight="0"/></worksheet>';
}
/** Брэндийн өнгө: толгой мөр `accent-100` дэвсгэр + `accent` доод зураас, анхааруулга magenta. */
const STYLES = `${HEAD}<styleSheet xmlns="${NS}">`
    + '<numFmts count="1"><numFmt numFmtId="164" formatCode="#,##0&quot;₮&quot;"/></numFmts>'
    + '<fonts count="4">'
    + '<font><sz val="11"/><name val="Calibri"/></font>'
    + '<font><b/><sz val="14"/><name val="Calibri"/></font>'
    + '<font><b/><sz val="11"/><name val="Calibri"/></font>'
    + '<font><sz val="11"/><color rgb="FFA8005C"/><name val="Calibri"/></font>'
    + '</fonts>'
    + '<fills count="3"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill>'
    + '<fill><patternFill patternType="solid"><fgColor rgb="FFE3F1F0"/><bgColor indexed="64"/></patternFill></fill></fills>'
    + '<borders count="2"><border><left/><right/><top/><bottom/><diagonal/></border>'
    + '<border><left/><right/><top/><bottom style="thin"><color rgb="FF0F7D77"/></bottom><diagonal/></border></borders>'
    + '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>'
    + '<cellXfs count="6">'
    + '<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>'
    + '<xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/>'
    + '<xf numFmtId="0" fontId="2" fillId="0" borderId="0" xfId="0" applyFont="1"/>'
    + '<xf numFmtId="0" fontId="2" fillId="2" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1"/>'
    + '<xf numFmtId="164" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>'
    + '<xf numFmtId="0" fontId="3" fillId="0" borderId="0" xfId="0" applyFont="1"/>'
    + '</cellXfs>'
    + '<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>'
    + '</styleSheet>';
const CONTENT_TYPES = `${HEAD}<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">`
    + `<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>`
    + '<Default Extension="xml" ContentType="application/xml"/>'
    + '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>'
    + '<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>'
    + '<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>'
    + '</Types>';
const ROOT_RELS = `${HEAD}<Relationships xmlns="${PKG_REL}">`
    + `<Relationship Id="rId1" Type="${REL}/officeDocument" Target="xl/workbook.xml"/></Relationships>`;
const WORKBOOK_RELS = `${HEAD}<Relationships xmlns="${PKG_REL}">`
    + `<Relationship Id="rId1" Type="${REL}/worksheet" Target="worksheets/sheet1.xml"/>`
    + `<Relationship Id="rId2" Type="${REL}/styles" Target="styles.xml"/></Relationships>`;
/** Нэг хуудастай `.xlsx` файлын байтууд. */
export function xlsxFile(sheet) {
    const workbook = `${HEAD}<workbook xmlns="${NS}" xmlns:r="${REL}">`
        + `<sheets><sheet name="${esc(sheet.name)}" sheetId="1" r:id="rId1"/></sheets></workbook>`;
    return zipStore([
        ['[Content_Types].xml', CONTENT_TYPES],
        ['_rels/.rels', ROOT_RELS],
        ['xl/workbook.xml', workbook],
        ['xl/_rels/workbook.xml.rels', WORKBOOK_RELS],
        ['xl/styles.xml', STYLES],
        ['xl/worksheets/sheet1.xml', sheetXml(sheet)],
    ]);
}
// ------------------------------------------------------------ ZIP (STORE)
function utf8(s) {
    const out = [];
    for (const ch of s) {
        let c = ch.codePointAt(0);
        if (c >= 0xd800 && c <= 0xdfff)
            c = 0xfffd; // ганц surrogate
        if (c < 0x80)
            out.push(c);
        else if (c < 0x800)
            out.push(0xc0 | (c >> 6), 0x80 | (c & 63));
        else if (c < 0x10000)
            out.push(0xe0 | (c >> 12), 0x80 | ((c >> 6) & 63), 0x80 | (c & 63));
        else
            out.push(0xf0 | (c >> 18), 0x80 | ((c >> 12) & 63), 0x80 | ((c >> 6) & 63), 0x80 | (c & 63));
    }
    return Uint8Array.from(out);
}
const CRC_TABLE = Uint32Array.from({ length: 256 }, (_, n) => {
    let c = n;
    for (let k = 0; k < 8; k++)
        c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    return c >>> 0;
});
/** ZIP-ийн CRC-32 (IEEE). */
export function crc32(bytes) {
    let c = 0xffffffff;
    for (const b of bytes)
        c = CRC_TABLE[(c ^ b) & 0xff] ^ (c >>> 8);
    return (c ^ 0xffffffff) >>> 0;
}
/** 1980-01-01 — тогтмол огноо: ижил оролт ижил байт өгнө. */
const DOS_DATE = 0x21;
/** Бичлэгийн толгой: `[байт, 16|32 бит, утга]` жагсаалтаас little-endian. */
function record(size, fields, tail) {
    const out = new Uint8Array(size + (tail?.length ?? 0));
    const dv = new DataView(out.buffer);
    for (const [at, bits, v] of fields) {
        if (bits === 16)
            dv.setUint16(at, v, true);
        else
            dv.setUint32(at, v, true);
    }
    if (tail)
        out.set(tail, size);
    return out;
}
/** Шахалтгүй ZIP. Excel STORE-ийг хүлээн авдаг; баримт хэдхэн арван KB. */
function zipStore(entries) {
    const parts = [];
    const central = [];
    let offset = 0;
    for (const [path, content] of entries) {
        const name = utf8(path);
        const data = utf8(content);
        const crc = crc32(data);
        // Локал толгой (0x04034b50) ба төв лавлахын бичлэг (0x02014b50)
        parts.push(record(30, [
            [0, 32, 0x04034b50], [4, 16, 20], [12, 16, DOS_DATE], [14, 32, crc],
            [18, 32, data.length], [22, 32, data.length], [26, 16, name.length],
        ], name), data);
        central.push(record(46, [
            [0, 32, 0x02014b50], [4, 16, 20], [6, 16, 20], [14, 16, DOS_DATE], [16, 32, crc],
            [20, 32, data.length], [24, 32, data.length], [28, 16, name.length], [42, 32, offset],
        ], name));
        offset += 30 + name.length + data.length;
    }
    const dirSize = central.reduce((a, c) => a + c.length, 0);
    const end = record(22, [
        [0, 32, 0x06054b50], [8, 16, entries.length], [10, 16, entries.length],
        [12, 32, dirSize], [16, 32, offset],
    ]);
    const all = [...parts, ...central, end];
    const out = new Uint8Array(all.reduce((a, c) => a + c.length, 0));
    all.reduce((at, c) => (out.set(c, at), at + c.length), 0);
    return out;
}
//# sourceMappingURL=xlsx.js.map